import React, { useMemo } from "react";
import { useI18n } from "../../hooks/i18n-hooks";
import { ListGroupItem, useListGroupRenderItem } from "../../components/ListGroup";
import { capitalizeFirstLetter, formatCurrency } from "../../utils";
import { useNavigation } from "@react-navigation/native";
import { RootStackParamList } from "../../navigations/RootNavigation";
import { StackScreenProps } from "@react-navigation/stack";
import { useQuery } from "@apollo/client/react";
import { FIND_MY_SALARIES } from "../../graphql/salaries";
import LoadingBoundary from "../../ui_atoms/LoadingBoundary";
import NoSalariesImage from "../../assets/images/no-salaries-image.svg";
import EmptyScreen from "../../components/EmptyScreen";
import Text from "../../components/Text";
import ScreenCard from "../ScreenCard";
import { useDateFormatter } from "../../hooks/date";
import { usePagination } from "../../hooks/use-pagination";
import { useTheme } from "../../hooks/use-theme";

type ScreenParams = StackScreenProps<RootStackParamList>;

const SALARIES_PAGE_SIZE = 12;

const Salaries = () => {
    const i18n = useI18n();
    const navigator = useNavigation<ScreenParams["navigation"]>();

    const { loading, error, data, refetch, fetchMore } = useQuery(FIND_MY_SALARIES, {
        fetchPolicy: "cache-and-network",
        variables: { limit: SALARIES_PAGE_SIZE },
    });

    const pagination = usePagination({ fetchMore });

    const mySalaries = useMemo(
        () => data?.findMySalaries.elements || [],
        [data?.findMySalaries.elements],
    );

    const salaryCount = data?.findMySalaries.count ?? 0;

    const { fonts, marginSize } = useTheme();

    const intlFormat = useDateFormatter();

    const items = useMemo(() => {
        return (mySalaries || []).map((salary): ListGroupItem<string> => {
            return {
                title: capitalizeFirstLetter(
                    intlFormat(new Date(salary.year, salary.month - 1), {
                        year: "numeric",
                        month: "long",
                    }),
                ),
                value: `${salary.year}-${salary.month}`,
                description: formatCurrency({ amountInCents: salary.netAmount }),
                tags: [
                    salary.payslips.length > 0
                        ? i18n.t("screens.user.salaries.payslip_count", {
                              count: salary.payslips.length,
                          })
                        : "",
                    salary.paidAt
                        ? capitalizeFirstLetter(
                              intlFormat(new Date(salary.paidAt), {
                                  day: "numeric",
                                  year: "numeric",
                                  month: "long",
                              }),
                          )
                        : "",
                ],
                onPress: () => {
                    if (salary.salaryAsset != null) {
                        navigator.navigate("SalaryDetails", {
                            salaryId: salary.id,
                        });
                    } else if (salary.payslips.length > 0) {
                        navigator.navigate("SalaryPayslips", {
                            salaryId: salary.id,
                        });
                    }
                },
            };
        });
    }, [intlFormat, mySalaries, navigator, i18n]);

    const renderRow = useListGroupRenderItem(items);

    const isInitialLoading = loading && !data;
    const showList = !error && !isInitialLoading && items.length > 0;

    return (
        <ScreenCard
            refresh={refetch}
            pagination={pagination}
            isContentContainerFlex={items.length === 0}
            headerComponent={
                showList ? (
                    <Text style={[fonts.labelSmall, { paddingHorizontal: marginSize }]}>
                        {i18n.t("screens.user.salaries.salaries_number", {
                            count: salaryCount,
                        })}
                    </Text>
                ) : undefined
            }
            virtualizedItems={showList ? items : undefined}
            renderItem={showList ? renderRow : undefined}
        >
            {showList ? undefined : (
                <LoadingBoundary
                    error={error ? i18n.t("screens.user.salaries.cannot_load_salaries") : undefined}
                    loading={isInitialLoading}
                >
                    {items.length === 0 ? (
                        <EmptyScreen
                            image={NoSalariesImage}
                            text={i18n.t("screens.user.salaries.no_salary")}
                        />
                    ) : null}
                </LoadingBoundary>
            )}
        </ScreenCard>
    );
};

export default Salaries;
